import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { SUBSCRIPTION_INVOICES } from '@/lib/routes/privateRoutes';
import { CHANGE_YOUR_PLAN } from '@/lib/settings/subscriptionInvoices/steps';

// Hooks
import useAcl from '@/utils/hooks/user/useAcl';

// Types
import type { SubscriptionType } from '@/types/pricingTypes/subscriptionType';
import type { ButtonProps } from '@/components/base/Button';

// Components
import Button from '@/components/base/Button';
import ButtonWithRequiredRoleTooltip from '@/components/composite/Button/ButtonWithRequiredRoleTooltip';

type UpgradeButtonProps = { plan: SubscriptionType } & Omit<ButtonProps, 'children'>;

const UpgradeButton = ({ plan, variant, icon, ...restProps }: UpgradeButtonProps) => {
    const { t } = useTranslation('commons');
    const isTrial = plan.product_key === 'trial';
    let finalVariant: ButtonProps['variant'] = 'secondary';
    let token = 'change_your_plan';
    let finalIcon: string;

    if (isTrial) {
        token = 'upgrade_your_plan';
    }
    if (!plan.product_key) {
        token = 'subscribe_to_plan';
    }
    if (variant) {
        finalVariant = variant;
    } else if (!variant && isTrial) {
        finalVariant = 'forward';
    }
    if (icon) {
        finalIcon = icon;
    } else if (!icon && isTrial) {
        finalIcon = 'arrow-top';
    }

    return (
        <Button variant={finalVariant} className="w-full" icon={finalIcon} {...restProps}>
            {t(token)}
        </Button>
    );
};

const ChangePlanButton = ({
    plan,
    size,
    variant,
    icon
}: {
    plan: SubscriptionType;
    size?: ButtonProps['size'];
    variant?: ButtonProps['variant'];
    icon?: ButtonProps['icon'];
}) => {
    const { hasAcl } = useAcl();
    if (!hasAcl(aclFeatures.account.billing.update)) {
        return (
            <ButtonWithRequiredRoleTooltip requiredRole="admin">
                {({ buttonDisabled }) => (
                    <UpgradeButton
                        icon={icon}
                        size={size}
                        plan={plan}
                        disabled={buttonDisabled}
                        variant={variant}
                        data-testid="button-no-right-to-upgrade"
                    />
                )}
            </ButtonWithRequiredRoleTooltip>
        );
    }

    return (
        <Link
            href={{
                pathname: SUBSCRIPTION_INVOICES,
                query: {
                    step: CHANGE_YOUR_PLAN
                }
            }}>
            <UpgradeButton
                icon={icon}
                plan={plan}
                data-testid="change-plan-button"
                size={size}
                variant={variant}
            />
        </Link>
    );
};

export default ChangePlanButton;
